import * as React from "react";
import { sendToServerForValidation } from "./utils";
import Form from "../commons/components/form/Form";
import { FormBackLinkProps } from "../commons/components/form/FormBackLink";
import AddressField from "../commons/components/form/input/AddressField";
import InputState from "../commons/model/form/InputState";
import TelInput from "../commons/components/form/input/TelInput";
import TextInput from "../commons/components/form/input/TextInput";
import { required } from "../commons/model/form/Validators";
import { getPayload } from "../commons/utils/form-utils";
import ConsentCheckboxInput from "../legal/ConsentCheckboxInput";
import LegalConsentProps from "../legal/LegalConsentProps";
import Country from "../commons/model/address/Country";

interface PracticeFormProps extends LegalConsentProps {
  validateEndpoint: string;
  countries: Country[];
  submitLabel: string;
  onSubmitSucceeded: (payload: any, form: { [key: string]: InputState }) => void;
  errorMsg?: string;
  displayTaxNumber: boolean;
  backLink: FormBackLinkProps;
  googleApiKey: string;
}

interface PracticeFormState {
  country: Country;
  errorMsg?: string;
}

export default class PracticeForm extends React.Component<PracticeFormProps, PracticeFormState> {
  constructor(props: PracticeFormProps) {
    super(props);
    this.state = {
      country: this.props.countries[0],
    };
    this.submit = this.submit.bind(this);
  }

  render() {
    return (
      <Form
        titleLabel={i18n["Form.DoctorRegistration.PracticeInformation.Title"]}
        submitLabel={this.props.submitLabel}
        submit={this.submit}
        generalErrorMsg={this.props.errorMsg || this.state.errorMsg}
        backLink={this.props.backLink}
      >
        <TextInput
          name="name"
          label={i18n["Global.PracticeName"]}
          placeholder={i18n["Form.DoctorRegistration.Placeholder.PracticeName"]}
          validators={[required()]}
        />
        <AddressField
          countries={this.props.countries}
          onCountryChange={(country) => this.setState({ country })}
          googleApiKey={this.props.googleApiKey}
        />
        <TelInput
          name="phoneNumber"
          label={i18n["Global.PhoneNumber"]}
          placeholder={i18n["Form.DoctorRegistration.Placeholder.PhoneNumber"]}
          selectedCountry={this.state.country}
          availableCountries={this.props.countries}
          validators={[required()]}
        />
        {this.props.displayTaxNumber && (
          <TextInput
            name="taxNumber"
            label={i18n["Global.TaxNumber"]}
            placeholder={i18n["Form.DoctorRegistration.Placeholder.TaxNumber"]}
            detail={i18n["Global.OptionalField"]}
          />
        )}
        {this.props.legalConsent && (
          <ConsentCheckboxInput
            name="legalDocuments"
            legalResources={this.props.legalConsent.legalResources}
            practiceToken={this.props.legalConsent.practiceToken}
            country={this.state.country}
            onError={this.props.legalConsent.onError}
          />
        )}
      </Form>
    );
  }

  private submit(form: { [key: string]: InputState }): Promise<void> {
    const payload = {
      practice: getPayload(form, ["name", "phoneNumber"]),
    };
    let address;
    if (form.place) {
      address = { ...form.place.value };
      address.country = address.country.name;
    } else {
      address = getPayload(form, [
        "number",
        "route",
        "city",
        "zipCode",
        "state",
        "country",
        "addressAdditionalInfo",
      ]);
    }
    payload.practice["address"] = address;
    if (this.props.displayTaxNumber) {
      payload["practice"]["taxNumber"] = form["taxNumber"].value || undefined;
    }

    return sendToServerForValidation(payload, this.props.validateEndpoint)
      .then(() => this.props.onSubmitSucceeded(payload, form))
      .catch((response) => {
        if (response.status == 400) {
          response.json().then((json) => {
            if (json["status"] == "ERROR_INVALID_COUNTRY") {
              form["country"].status.setError(i18n["Form.DoctorRegistration.Error.Country"]);
            } else {
              this.setState({
                errorMsg: i18n["Form.DoctorRegistration.Error.BadRequest"],
              });
            }
          });
        }
        throw response;
      });
  }
}
